import { InPersonSession } from '@app/features/inPerson/types';

import useInPersonSessionStart from '../useInPersonSessionStart';
import useInPersonSessionRestart from '../useRestartInPersonSession';

export default function useInPersonSession({ documentId }: { documentId: string }) {
  const {
    mutateAsync: startSession,
    isLoading: startLoading,
    error: startSessionError,
    reset: resetStart,
  } = useInPersonSessionStart();
  const {
    mutateAsync: restartInPersonSession,
    reset: resetRestart,
    isLoading: restartLoading,
    error: restartSessionError,
  } = useInPersonSessionRestart();

  async function startInPersonSession({
    onShouldRestartSession,
    onSuccessStartSession,
    onError,
    hostParticipantId,
    participantId,
  }: {
    onShouldRestartSession: (prevSession: InPersonSession) => Promise<boolean>;
    onSuccessStartSession: () => void;
    onError?: () => void;
    hostParticipantId: string;
    participantId: string;
  }) {
    try {
      const inPersonSession = await startSession({
        participantId,
        documentId,
      });

      const shouldRestartSession =
        inPersonSession.occupied &&
        !(
          inPersonSession.session.hostId === hostParticipantId &&
          inPersonSession.session.signerId === participantId
        );

      if (shouldRestartSession) {
        if (!(await onShouldRestartSession(inPersonSession.session))) {
          return;
        }
        await restartInPersonSession({
          participantId,
          documentId,
        });
      }

      onSuccessStartSession();
    } catch {
      onError?.();
    }
  }

  function reset() {
    resetStart();
    resetRestart();
  }

  return {
    startInPersonSession,
    isLoading: startLoading || restartLoading,
    startSessionError,
    restartSessionError,
    reset,
  };
}
